import React, { useState, useEffect, useCallback } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '../ui/card';
import { Button } from '../ui/button';
import {
  Activity,
  User,
  Heart,
  Droplets,
  Scale,
  TrendingUp,
  Loader2,
  Calendar
} from 'lucide-react';
import axios from 'axios';
import { toast } from 'sonner';
import { useAuth } from '../../contexts/AuthContext';

const API_URL = process.env.REACT_APP_BACKEND_URL + '/api';

const MyPerformanceCard = ({ language = 'fr' }) => {
  const { token, user } = useAuth();
  const [loading, setLoading] = useState(true);
  const [stats, setStats] = useState(null);
  const [error, setError] = useState(false);

  const fetchMyStats = useCallback(async () => {
    // If no user or no token, don't show this card
    if (!user?.id || !token) {
      setLoading(false);
      setError(true);
      return;
    }
    
    setLoading(true);
    setError(false);
    try {
      // Get stats for current month
      const now = new Date();
      const startOfMonth = new Date(now.getFullYear(), now.getMonth(), 1);
      
      const response = await axios.get(`${API_URL}/health/stats/agent/${user.id}`, {
        headers: { Authorization: `Bearer ${token}` },
        params: {
          start_date: startOfMonth.toISOString().split('T')[0],
          end_date: now.toISOString().split('T')[0]
        }
      });
      setStats(response.data);
    } catch (error) {
      console.error('Error fetching my stats:', error);
      setError(true);
      // Silently fail - this is a non-critical feature
    } finally {
      setLoading(false);
    }
  }, [token, user?.id]);

  useEffect(() => {
    fetchMyStats();
  }, [fetchMyStats]);

  // Don't render if no user or error occurred
  if (error || !user?.id) {
    return null;
  }

  if (loading) {
    return (
      <Card className="border-blue-100">
        <CardContent className="p-6 flex items-center justify-center">
          <Loader2 className="w-6 h-6 animate-spin text-blue-600" />
        </CardContent>
      </Card>
    );
  }

  if (!stats) {
    return null; // Don't show if no data available
  }

  const formatDate = (dateStr) => {
    if (!dateStr) return '-';
    return new Date(dateStr).toLocaleDateString('fr-FR', {
      day: '2-digit',
      month: '2-digit'
    });
  };

  return (
    <Card className="border-blue-200 bg-gradient-to-br from-blue-50 to-white">
      <CardHeader className="pb-2">
        <CardTitle className="text-base flex items-center gap-2 text-blue-800">
          <TrendingUp className="w-5 h-5" />
          {language === 'fr' ? 'Ma Performance SADAHealth' : 'My SADAHealth Performance'}
          <span className="text-xs font-normal text-gray-500 ml-auto">
            {language === 'fr' ? 'Ce mois' : 'This month'}
          </span>
        </CardTitle>
      </CardHeader>
      <CardContent className="pt-0">
        <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
          {/* Patients created */}
          <div className="bg-white rounded-lg p-3 border border-blue-100 text-center">
            <User className="w-5 h-5 mx-auto mb-1 text-purple-600" />
            <p className="text-2xl font-bold text-gray-900">{stats.patients_created || 0}</p>
            <p className="text-xs text-gray-500">
              {language === 'fr' ? 'Patients créés' : 'Patients created'}
            </p>
          </div>
          
          {/* Total measurements */}
          <div className="bg-white rounded-lg p-3 border border-blue-100 text-center">
            <Activity className="w-5 h-5 mx-auto mb-1 text-green-600" />
            <p className="text-2xl font-bold text-gray-900">{stats.total_measurements || 0}</p>
            <p className="text-xs text-gray-500">
              {language === 'fr' ? 'Mesures' : 'Measurements'}
            </p>
          </div>
          
          {/* BP measurements */}
          <div className="bg-white rounded-lg p-3 border border-blue-100 text-center">
            <Heart className="w-5 h-5 mx-auto mb-1 text-red-500" />
            <p className="text-2xl font-bold text-gray-900">{stats.bp_measurements || 0}</p>
            <p className="text-xs text-gray-500">
              {language === 'fr' ? 'Tensions' : 'BP checks'}
            </p>
          </div>
          
          {/* Glycemia measurements */}
          <div className="bg-white rounded-lg p-3 border border-blue-100 text-center">
            <Droplets className="w-5 h-5 mx-auto mb-1 text-blue-500" />
            <p className="text-2xl font-bold text-gray-900">{stats.glycemia_measurements || 0}</p>
            <p className="text-xs text-gray-500">
              {language === 'fr' ? 'Glycémies' : 'Glucose'}
            </p>
          </div>
        </div>
        
        {/* Last activity */}
        {stats.last_activity_at && (
          <div className="mt-3 text-xs text-gray-500 text-center flex items-center justify-center gap-1">
            <Calendar className="w-3 h-3" />
            {language === 'fr' ? 'Dernière activité :' : 'Last activity:'} {formatDate(stats.last_activity_at)}
          </div>
        )}
      </CardContent>
    </Card>
  );
};

export default MyPerformanceCard;
